import React, { useEffect, useState } from 'react'
import { adminProcessService } from '../../../services/admin/process'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { Box, Button, Card, Container, Tooltip, Typography } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import { useNavigate } from 'react-router'
import { categoryService } from '../../../services/categoryService'
import KeywordList from '../components/KeywordList'
import CreateProcessTemplateDialog from '../components/CreateProcessTemplateDialog'
import SchemaIcon from '@mui/icons-material/Schema'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { useSelectedProcess } from '../adminStore'
import { toast } from 'react-toastify'
import ConfirmationDialog from '../Workflow/components/ConfirmationDialog'
import ActivityList from './components/ActivityList'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import DelegateTaskDialog from '../components/DelegateTaskDialog'
import PreviousActivityDialogueBox from '../components/PreviousActivityDialogueBox'
import { adminTeamsService } from '../../../services/admin/teams'
import OptionMenu from '../../../components/core-ui/OptionMenu'
import ManageHolidays from '../components/ManageHolidays'

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import Diversity2Icon from '@mui/icons-material/Diversity2'
import ListIcon from '@mui/icons-material/List'
import EventIcon from '@mui/icons-material/Event'
import MoveDownIcon from '@mui/icons-material/MoveDown'
import UndoIcon from '@mui/icons-material/Undo'
import DnsIcon from '@mui/icons-material/Dns'
import EditNotificationsIcon from '@mui/icons-material/EditNotifications'
import DescriptionIcon from '@mui/icons-material/Description'
import { useResponsiveTypography } from '../../../utility/responsiveTypography'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'

const defaultCategory = { id: 0, value: 'All' }

const AdminLandingPage = () => {
  const { admintitle, adminsubtitle } = useResponsiveTypography()

  const navigate = useNavigate()
  const { user } = useAuthContext()
  const setSelectedProcess = useSelectedProcess((state) => state.setSelectedProcess)
  const selectedProcess = useSelectedProcess((state) => state.selectedProcess)

  const [processes, setProcesses] = useState([])
  const [users, setUsers] = useState([])
  const [categoryDetails, setCategoryDetails] = useState({})
  const [usedCategories, setUsedCategories] = useState([])
  const [selectedKeywords, setSelectedKeywords] = useState([defaultCategory])
  const [showCreateActivityDialog, setShowCreateActivityDialog] = useState(false)
  const [selectedForDeletion, setForDeletion] = useState(null)
  const [opened, setOpened] = useState({})
  const [delegationDialogOpen, setDelegationDialogOpen] = useState(false)
  const [previousActivityDialogueBox, setPreviousActivityDialogueBox] = useState(false)
  const [manageHolidaysOpen, setManageHolidaysOpen] = useState(false)

  const handleSelect = (selected) => {
    if (selected.id == defaultCategory.id) {
      setSelectedKeywords([defaultCategory])
    } else {
      const isAlreadyPresent = selectedKeywords.find((it) => it.id == selected.id)
      if (isAlreadyPresent && selectedKeywords.length == 1) {
        setSelectedKeywords([defaultCategory])
      } else if (isAlreadyPresent && selectedKeywords.length > 1) {
        setSelectedKeywords([...selectedKeywords.filter((key) => key.id != selected.id)])
      } else {
        setSelectedKeywords([
          ...selectedKeywords.filter((key) => key.id != defaultCategory.id),
          selected,
        ])
      }
    }
  }

  const fetchAllProcesses = async () => {
    const processes = await adminProcessService.getProcesses(user)
    let transformedItems = processes.reduce((acc, item) => {
      const existingItem = acc.find((x) => x.id === item.id)
      if (existingItem) {
        existingItem.category_ids.push(item.process_category_id)
      } else {
        acc.push({ ...item, category_ids: [item.process_category_id] })
      }
      return acc
    }, [])
    transformedItems.sort((a, b) => b.sequence - a.sequence)
    setProcesses(transformedItems)
  }

  useEffect(() => {
    ;(async () => {
      if (user) {
        // get all users
        const users = await adminTeamsService.getUsers(user)
        setUsers(users)

        // getting all processes
        await fetchAllProcesses()

        // getting category details
        const categoryDetails = await categoryService.getCategoryDetails(user)
        setCategoryDetails(categoryDetails)

        // find all category to filter
        const allCategory = categoryDetails?.processCategories?.reduce((acc, cur) => {
          if (acc.find((ai) => cur.cat_id == ai.id) == undefined) {
            return [...acc, { id: cur.cat_id, value: cur.name }]
          } else {
            return [...acc]
          }
        }, [])
        allCategory.unshift(defaultCategory)
        setUsedCategories(allCategory)
      }
    })()
  }, [user])

  const deleteProcess = async (processTemplate) => {
    // call delete process
    try {
      const res = await adminProcessService.deleteProcess(user, processTemplate.id)
      await fetchAllProcesses()
      if (res.status == 'error') {
        throw new Error(res.message)
      }
      setForDeletion(null)
    } catch (error) {
      toast.error(error.message)
    }
  }

  const options = [
    { id: 'new-process', title: 'New Process', icon: <AddCircleOutlineIcon fontSize="small" /> },
    { id: 'manage-team', title: 'Teams', icon: <Diversity2Icon fontSize="small" /> },
    { id: 'manage-list', title: 'Lists', icon: <ListIcon fontSize="small" /> },
    {
      id: 'task-delegation',
      title: 'Delegate Task',
      icon: <MoveDownIcon fontSize="small" />,
    },
    {
      id: 'previous-activity',
      title: 'Previous Activities',
      icon: <UndoIcon fontSize="small" />,
    },
    { id: 'manage-holiday', title: 'Holidays', icon: <EventIcon fontSize="small" /> },
    { id: 'manage-server-action', title: 'Server Actions', icon: <DnsIcon fontSize="small" /> },
    {
      id: 'manage-notification-settings',
      title: 'Notification Settings',
      icon: <EditNotificationsIcon fontSize="small" />,
    },
    {
      id: 'manage-document-templates',
      title: 'Document Templates',
      icon: <DescriptionIcon fontSize="small" />,
    },
  ]
  const handleOptionSelect = (optionId) => {
    switch (optionId) {
      case 'new-process':
        setShowCreateActivityDialog(true)
        break
      case 'manage-team':
        navigate('/admin/user-team')
        break
      case 'manage-list':
        navigate('/admin/lists')
        break
      case 'task-delegation':
        setDelegationDialogOpen(true)
        break
      case 'previous-activity':
        setPreviousActivityDialogueBox(true)
        break
      case 'manage-holiday':
        setManageHolidaysOpen(true)
        break
      case 'manage-server-action':
        navigate('/admin/server-actions')
        break
      case 'manage-notification-settings':
        navigate('/admin/notification-settings')
        break
      case 'manage-document-templates':
        navigate('/admin/docuement-templates')
        break
      default:
        window.alert('No action associated!')
        break
    }
  }

  return (
    <>
      <Container style={{ paddingLeft: '10px', paddingRight: '10px', paddingTop: '10px' }}>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0px 8px',
          }}
        >
          <Typography
            className="textPrimary fontWeight700"
            variant="h5"
            fontWeight="bold"
            style={{ fontSize: admintitle, marginTop: '10px' }}
          >
            All Processes
          </Typography>

          <OptionMenu
            className="more-action"
            options={options}
            onOptionSelect={handleOptionSelect}
            tooltipContent="More actions"
          />
        </Box>

        <Box style={{ position: 'sticky', top: 0, marginTop: '-5px' }}>
          <KeywordList
            keywords={usedCategories}
            selectedKeywords={selectedKeywords}
            onSelect={handleSelect}
          />
        </Box>
        <Box style={{ display: 'flex' }}>
          <Box style={{ width: '100%', position: 'relative' }}>
            {processes
              .filter((process) => {
                const selectedCategoryIds = selectedKeywords.map((it) => it.id)
                return (
                  selectedCategoryIds.includes(process.process_category_id) ||
                  (selectedCategoryIds.includes(0) && selectedCategoryIds.length <= 1)
                )
              })
              .reduce((acc, cur) => {
                return acc.find((it) => it.id == cur.id) == undefined ? [...acc, cur] : acc
              }, [])
              .map((it) => {
                return (
                  <Card
                    className="admin-cardbox"
                    elevation={1}
                    style={{ padding: 7, margin: 8 }}
                    key={it.id}
                  >
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Tooltip title={it.description}>
                          <Typography variant="h6" style={{ margin: 8, fontSize: adminsubtitle }}>
                            {it.id}. {it.process_template_name}
                          </Typography>
                        </Tooltip>
                      </Box>

                      <Box className="admin-groupicon" style={{ display: 'flex', columnGap: 8 }}>
                        <Tooltip title="Open Workflow">
                          <IconButton
                            onClick={() => navigate('/admin/process-workflow/' + it.id)}
                            style={{ height: 32, width: 32, backgroundColor: '#ede8ff' }}
                            size="small"
                          >
                            <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit Process">
                          <IconButton
                            onClick={() => setSelectedProcess(it)}
                            style={{ height: 32, width: 32, backgroundColor: '#ede8ff' }}
                            size="small"
                          >
                            <ModeEditOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete Process">
                          <IconButton
                            onClick={() => setForDeletion(it)}
                            style={{ height: 32, width: 32, backgroundColor: '#ede8ff' }}
                            size="small"
                          >
                            <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="View Activities">
                          <IconButton
                            onClick={() =>
                              setOpened({
                                ...opened,
                                [it.id]: !opened[it.id],
                              })
                            }
                            style={{ height: 32, width: 32, backgroundColor: '#ede8ff' }}
                            size="small"
                          >
                            {opened[it.id] ? (
                              <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    {opened[it.id] && <ActivityList process={it} />}
                  </Card>
                )
              })}
          </Box>
        </Box>
      </Container>
      <CreateProcessTemplateDialog
        show={showCreateActivityDialog || selectedProcess != null}
        setShow={(v) => {
          setShowCreateActivityDialog(v)
          setSelectedProcess(null)
        }}
        reloadProcesses={fetchAllProcesses}
      />
      <ConfirmationDialog
        title={`Delete Process '${selectedForDeletion?.process_template_name}'`}
        message={
          'Be careful! It will delete complete process including all activities, pages, fields and all other settings.'
        }
        open={selectedForDeletion != null}
        onClose={() => setForDeletion(null)}
        onConfirm={() => deleteProcess(selectedForDeletion)}
        reloadProcesses={fetchAllProcesses}
      />
      <DelegateTaskDialog
        users={users}
        processes={processes}
        open={delegationDialogOpen}
        onClose={() => setDelegationDialogOpen(false)}
      />
      <PreviousActivityDialogueBox
        processes={processes}
        open={previousActivityDialogueBox}
        onClose={() => setPreviousActivityDialogueBox(false)}
      />
      <ManageHolidays open={manageHolidaysOpen} onClose={() => setManageHolidaysOpen(false)} />
    </>
  )
}

export default AdminLandingPage
